/**
 * Breathing Exposure Calculator
 *
 * Calculates real pollution dosage based on:
 * - Breathing rate (varies by exercise intensity/pace)
 * - Pollutant concentration (PM2.5 µg/m³)
 * - Duration of exposure
 *
 * Key insight: A runner breathing 40-60 L/min inhales 10-20x more
 * pollutants than a person at rest (6-8 L/min).
 *
 * Formula: Dose = Breathing Rate × Concentration × Duration
 * Units: µg inhaled = (m³/min) × (µg/m³) × (minutes)
 */

import type { AQISample, RunSnapshot } from '@/types/running';

// ============== TYPES ==============

export interface ExposureMetrics {
  // Total PM2.5 inhaled during the run (micrograms)
  totalPM25Inhaled: number;
  // Average breathing rate during the run (L/min)
  averageBreathingRate: number;
  // Total air volume breathed (liters)
  totalAirVolume: number;
  // Exposure score 0-100 (0 = cleanest possible, 100 = hazardous)
  exposureScore: number;
  // Health impact category
  healthImpact: 'minimal' | 'low' | 'moderate' | 'high' | 'severe';
  // Equivalent exposure: "same as breathing X hours of rest in this air"
  restEquivalentHours: number;
  // How much cleaner this run could have been on the best route
  cleanerAlternativePct: number | null;
  // Per-km breakdown
  perKmExposure: PerKmExposure[];
  // Comparison text
  comparisonText: string;
}

export interface PerKmExposure {
  km: number;
  pm25: number;
  breathingRate: number;
  dose: number; // µg inhaled in this km
  quality: 'excellent' | 'good' | 'moderate' | 'poor' | 'hazardous';
}

export interface CumulativeExposure {
  // Total PM2.5 inhaled across all runs (µg)
  totalPM25: number;
  // Number of runs tracked
  totalRuns: number;
  // Total running duration (seconds)
  totalDuration: number;
  // Total distance (meters)
  totalDistance: number;
  // Average exposure per run
  averagePerRun: number;
  // Weekly trend (last 4 weeks)
  weeklyTrend: WeeklyExposure[];
  // Best and worst runs
  cleanestRunPM25: number;
  dirtiestRunPM25: number;
  // Health equivalence
  totalRestEquivalentHours: number;
}

export interface WeeklyExposure {
  weekLabel: string;
  totalPM25: number;
  totalRuns: number;
  totalDistance: number;
  averagePM25PerRun: number;
}

// ============== BREATHING RATE MODEL ==============

/**
 * Estimate breathing rate based on running pace.
 *
 * Research-based values:
 * - Rest: 6-8 L/min
 * - Walking (8-9 min/km): 20-25 L/min
 * - Easy jog (7-8 min/km): 30-40 L/min
 * - Moderate run (5:30-6:30 min/km): 40-60 L/min
 * - Hard run (4:30-5:30 min/km): 60-80 L/min
 * - Sprint/race (<4:30 min/km): 80-120 L/min
 *
 * @param paceSecondsPerKm - Running pace in seconds per km
 * @returns Breathing rate in liters per minute
 */
export function estimateBreathingRate(paceSecondsPerKm: number): number {
  if (paceSecondsPerKm <= 0) return 7; // Rest

  const paceMinPerKm = paceSecondsPerKm / 60;

  if (paceMinPerKm > 12) return 15;      // Very slow walk
  if (paceMinPerKm > 9) return 22;       // Brisk walk
  if (paceMinPerKm > 7.5) return 35;     // Easy jog
  if (paceMinPerKm > 6) return 50;       // Moderate run (typical casual ~6:30)
  if (paceMinPerKm > 5) return 65;       // Tempo run
  if (paceMinPerKm > 4) return 85;       // Hard/race
  return 110;                             // Sprint
}

/**
 * Get breathing rate for stationary activities
 */
export function getStationaryBreathingRate(activityType: string): number {
  switch (activityType) {
    case 'yoga': return 10;
    case 'strength': return 25;
    case 'swim': return 35;
    case 'treadmill': return 50;
    default: return 7;
  }
}

// ============== EXPOSURE CALCULATION ==============

/**
 * Calculate detailed exposure metrics for a completed run.
 *
 * @param durationSeconds - Total run duration
 * @param distanceMeters - Total distance
 * @param aqiSamples - Air quality samples collected during the run
 * @param snapshots - Run snapshots with pace data
 * @param activityType - Type of activity
 */
export function calculateExposureMetrics(
  durationSeconds: number,
  distanceMeters: number,
  aqiSamples: AQISample[],
  snapshots: RunSnapshot[],
  activityType: string,
): ExposureMetrics {
  const durationMinutes = durationSeconds / 60;
  const distanceKm = distanceMeters / 1000;
  const isStationary = ['yoga', 'strength', 'treadmill', 'swim'].includes(activityType);

  // Get average PM2.5 from samples
  const pm25Values = aqiSamples
    .map(s => s.pm25)
    .filter((v): v is number => v !== null && v > 0);
  const avgPM25 = pm25Values.length > 0
    ? pm25Values.reduce((a, b) => a + b, 0) / pm25Values.length
    : estimatePM25FromAQI(aqiSamples.length > 0
        ? aqiSamples.reduce((a, s) => a + s.aqi, 0) / aqiSamples.length
        : 50);

  // Calculate breathing rate
  let avgBreathingRate: number;
  if (isStationary) {
    avgBreathingRate = getStationaryBreathingRate(activityType);
  } else if (snapshots.length > 0) {
    // Use pace from snapshots for more accurate breathing rate
    const paces = snapshots.map(s => s.pace).filter(p => p > 0);
    const avgPace = paces.length > 0 ? paces.reduce((a, b) => a + b, 0) / paces.length : 0;
    avgBreathingRate = avgPace > 0 ? estimateBreathingRate(avgPace) : 50;
  } else if (distanceKm > 0.01) {
    const avgPace = durationSeconds / distanceKm;
    avgBreathingRate = estimateBreathingRate(avgPace);
  } else {
    avgBreathingRate = 50; // Default moderate run
  }

  // Core calculation: Dose = Breathing Rate × Concentration × Duration
  // Convert L/min to m³/min: 1 L = 0.001 m³
  const breathingRateM3 = avgBreathingRate / 1000;
  const totalPM25Inhaled = breathingRateM3 * avgPM25 * durationMinutes;

  // Total air volume breathed
  const totalAirVolume = avgBreathingRate * durationMinutes;

  // Resting equivalent: how many hours at rest would give same dose
  const restBreathingRate = 7 / 1000; // 7 L/min at rest in m³/min
  const restEquivalentMinutes = totalPM25Inhaled / (restBreathingRate * avgPM25 || 1);
  const restEquivalentHours = restEquivalentMinutes / 60;

  // Exposure score (0-100)
  // Based on WHO guidelines: 24h mean PM2.5 should be <15 µg/m³
  // For exercise, we weight by breathing rate multiplier
  const breathingMultiplier = avgBreathingRate / 7;
  const effectivePM25 = avgPM25 * breathingMultiplier;
  const exposureScore = Math.min(100, Math.round(
    (effectivePM25 / 150) * 100 // 150 = very high effective exposure
  ));

  // Health impact
  const healthImpact = getHealthImpact(totalPM25Inhaled, durationMinutes);

  // Per-km breakdown
  const perKmExposure = calculatePerKmExposure(
    distanceKm, durationSeconds, aqiSamples, snapshots, activityType
  );

  // Comparison text
  const comparisonText = generateComparisonText(
    totalPM25Inhaled, avgPM25, avgBreathingRate, durationMinutes
  );

  return {
    totalPM25Inhaled: Math.round(totalPM25Inhaled * 100) / 100,
    averageBreathingRate: Math.round(avgBreathingRate),
    totalAirVolume: Math.round(totalAirVolume),
    exposureScore,
    healthImpact,
    restEquivalentHours: Math.round(restEquivalentHours * 10) / 10,
    cleanerAlternativePct: null, // Would need route comparison data
    perKmExposure,
    comparisonText,
  };
}

// ============== HELPERS ==============

function estimatePM25FromAQI(aqi: number): number {
  // Rough conversion: AQI 50 ≈ 12 µg/m³, AQI 100 ≈ 35 µg/m³
  if (aqi <= 50) return aqi * 0.24;
  if (aqi <= 100) return 12 + (aqi - 50) * 0.46;
  if (aqi <= 150) return 35 + (aqi - 100) * 0.4;
  return 55 + (aqi - 150) * 0.95;
}

function getHealthImpact(
  totalPM25: number,
  durationMinutes: number
): 'minimal' | 'low' | 'moderate' | 'high' | 'severe' {
  // Normalized dose per hour of exercise
  const dosePerHour = durationMinutes > 0 ? (totalPM25 / durationMinutes) * 60 : 0;

  if (dosePerHour < 0.5) return 'minimal';
  if (dosePerHour < 1.5) return 'low';
  if (dosePerHour < 3.0) return 'moderate';
  if (dosePerHour < 6.0) return 'high';
  return 'severe';
}

function calculatePerKmExposure(
  totalKm: number,
  durationSeconds: number,
  aqiSamples: AQISample[],
  snapshots: RunSnapshot[],
  activityType: string,
): PerKmExposure[] {
  if (totalKm < 0.1 || aqiSamples.length === 0) return [];

  const perKm: PerKmExposure[] = [];
  const fullKms = Math.floor(totalKm);
  const kmCount = Math.max(1, fullKms);

  for (let km = 1; km <= kmCount; km++) {
    const progress = km / totalKm;
    const sampleIndex = Math.min(
      Math.floor(progress * aqiSamples.length),
      aqiSamples.length - 1
    );
    const sample = aqiSamples[sampleIndex];

    const pm25 = sample.pm25 ?? estimatePM25FromAQI(sample.aqi);

    // Find closest snapshot for pace
    const snapshotIndex = Math.min(
      Math.floor(progress * snapshots.length),
      snapshots.length - 1
    );
    const pace = snapshots[snapshotIndex]?.pace || (durationSeconds / totalKm);

    const breathingRate = ['yoga', 'strength', 'swim', 'treadmill'].includes(activityType)
      ? getStationaryBreathingRate(activityType)
      : estimateBreathingRate(pace);

    // Time for this km in minutes
    const kmDurationMinutes = pace / 60;
    const dose = (breathingRate / 1000) * pm25 * kmDurationMinutes;

    perKm.push({
      km,
      pm25: Math.round(pm25 * 10) / 10,
      breathingRate: Math.round(breathingRate),
      dose: Math.round(dose * 100) / 100,
      quality: pm25 <= 12 ? 'excellent'
        : pm25 <= 25 ? 'good'
        : pm25 <= 35 ? 'moderate'
        : pm25 <= 55 ? 'poor'
        : 'hazardous',
    });
  }

  return perKm;
}

function generateComparisonText(
  totalPM25: number,
  avgPM25: number,
  breathingRate: number,
  durationMinutes: number,
): string {
  const multiplier = Math.round(breathingRate / 7);
  const cigaretteEquivalent = totalPM25 / 22; // ~22 µg PM2.5 per cigarette

  if (avgPM25 <= 12) {
    return `Great air! You breathed ${multiplier}x more than at rest, but the air was clean. Minimal pollution exposure.`;
  }

  if (avgPM25 <= 35) {
    return `Moderate air. At ${Math.round(breathingRate)} L/min for ${Math.round(durationMinutes)} min, you inhaled ${totalPM25.toFixed(1)} µg of PM2.5 — equivalent to ${Math.round(durationMinutes * multiplier / 60)} hours of rest exposure.`;
  }

  if (cigaretteEquivalent >= 1) {
    return `Caution: You inhaled ${totalPM25.toFixed(1)} µg of PM2.5, roughly equivalent to ${cigaretteEquivalent.toFixed(1)} cigarettes. Consider running earlier when AQI is lower.`;
  }

  return `You inhaled ${totalPM25.toFixed(1)} µg of PM2.5 at ${Math.round(breathingRate)} L/min breathing rate. That's ${multiplier}x what you'd breathe at rest.`;
}

// ============== CUMULATIVE EXPOSURE ==============

/**
 * Calculate cumulative exposure from activity history.
 * Uses locally stored activity data.
 */
export function calculateCumulativeExposure(
  activities: Array<{
    duration_seconds: number;
    distance_meters: number;
    aqi_data: { average_aqi: number; aqi_samples: AQISample[] } | null;
    pace_seconds_per_km: number;
    activity_type: string;
    created_at: string;
  }>
): CumulativeExposure {
  let totalPM25 = 0;
  let totalDuration = 0;
  let totalDistance = 0;
  let cleanestPM25 = Infinity;
  let dirtiestPM25 = 0;

  // Calculate per-activity exposure
  for (const activity of activities) {
    const avgAqi = activity.aqi_data?.average_aqi ?? 50;
    const avgPM25 = estimatePM25FromAQI(avgAqi);
    const durationMinutes = activity.duration_seconds / 60;
    const breathingRate = estimateBreathingRate(activity.pace_seconds_per_km);
    const dose = (breathingRate / 1000) * avgPM25 * durationMinutes;

    totalPM25 += dose;
    totalDuration += activity.duration_seconds;
    totalDistance += activity.distance_meters;

    if (dose < cleanestPM25) cleanestPM25 = dose;
    if (dose > dirtiestPM25) dirtiestPM25 = dose;
  }

  // Weekly trend (last 4 weeks)
  const weeklyTrend = calculateWeeklyTrend(activities);

  const restEquivalentHours = totalPM25 / ((7 / 1000) * 15 * 60); // 7L/min, 15µg/m³ moderate air

  return {
    totalPM25: Math.round(totalPM25 * 100) / 100,
    totalRuns: activities.length,
    totalDuration,
    totalDistance,
    averagePerRun: activities.length > 0 ? Math.round((totalPM25 / activities.length) * 100) / 100 : 0,
    weeklyTrend,
    cleanestRunPM25: cleanestPM25 === Infinity ? 0 : Math.round(cleanestPM25 * 100) / 100,
    dirtiestRunPM25: Math.round(dirtiestPM25 * 100) / 100,
    totalRestEquivalentHours: Math.round(restEquivalentHours * 10) / 10,
  };
}

function calculateWeeklyTrend(
  activities: Array<{
    duration_seconds: number;
    distance_meters: number;
    aqi_data: { average_aqi: number; aqi_samples: AQISample[] } | null;
    pace_seconds_per_km: number;
    created_at: string;
  }>
): WeeklyExposure[] {
  const weeks: WeeklyExposure[] = [];
  const now = new Date();

  for (let w = 3; w >= 0; w--) {
    const weekStart = new Date(now);
    weekStart.setDate(weekStart.getDate() - (w + 1) * 7);
    const weekEnd = new Date(now);
    weekEnd.setDate(weekEnd.getDate() - w * 7);

    const weekActivities = activities.filter(a => {
      const date = new Date(a.created_at);
      return date >= weekStart && date < weekEnd;
    });

    let weekPM25 = 0;
    let weekDistance = 0;

    for (const activity of weekActivities) {
      const avgAqi = activity.aqi_data?.average_aqi ?? 50;
      const avgPM25 = estimatePM25FromAQI(avgAqi);
      const breathingRate = estimateBreathingRate(activity.pace_seconds_per_km);
      const durationMinutes = activity.duration_seconds / 60;
      weekPM25 += (breathingRate / 1000) * avgPM25 * durationMinutes;
      weekDistance += activity.distance_meters;
    }

    const weekLabel = w === 0 ? 'This week'
      : w === 1 ? 'Last week'
      : `${w + 1} weeks ago`;

    weeks.push({
      weekLabel,
      totalPM25: Math.round(weekPM25 * 100) / 100,
      totalRuns: weekActivities.length,
      totalDistance: weekDistance,
      averagePM25PerRun: weekActivities.length > 0
        ? Math.round((weekPM25 / weekActivities.length) * 100) / 100
        : 0,
    });
  }

  return weeks;
}
